import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { SingleResponse } from '@/shared/types/responseTypes';
import type { MfaStatus, RecoveryCodesResult, TotpSetup } from '@/shared/types/mfaTypes';

const MFA_STATUS_QUERY_KEY = ['auth-mfa-status'] as const;

const unwrapData = <T>(response: SingleResponse<T> | T): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as SingleResponse<T>).data;
  }
  return response as T;
};

export const useMfaStatus = (enabled: boolean) => {
  return useQuery({
    queryKey: MFA_STATUS_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get<SingleResponse<MfaStatus> | MfaStatus>('/api/v1/mfa');
      return unwrapData(response);
    },
    enabled,
  });
};

export const useTotpSetup = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<SingleResponse<TotpSetup> | TotpSetup>(
        '/api/v1/mfa/totp/setup',
      );
      return unwrapData(response);
    },
  });
};

export const useTotpConfirm = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const response = await apiClient.post<SingleResponse<RecoveryCodesResult> | RecoveryCodesResult>(
        '/api/v1/mfa/totp/confirm',
        { body: { code } },
      );
      return unwrapData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MFA_STATUS_QUERY_KEY });
    },
  });
};

export const useRotateRecoveryCodes = () => {
  return useMutation({
    mutationFn: async (code: string) => {
      const response = await apiClient.post<SingleResponse<RecoveryCodesResult> | RecoveryCodesResult>(
        '/api/v1/mfa/recovery/rotate',
        { body: { code } },
      );
      return unwrapData(response);
    },
  });
};

export const useDisableTotp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ password, code, factor }: { password: string; code: string; factor: 'TOTP' | 'RECOVERY' }) => {
      return apiClient.delete<APIMessageResponse>('/api/v1/mfa/totp', {
        body: { password, code, factor },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MFA_STATUS_QUERY_KEY });
    },
  });
};
